/**
 * The page chrome around every screen of the sign-in gate: the skip link, the Microsoft /
 * Law Enforcement Portal header, and the main region the screen sits in. Ported from the
 * le-portal-ui-v3 prototype (src/App.css).
 *
 * Screens put their heading at `#login-content-title`, which the skip link targets.
 */
import type { ReactNode } from 'react';
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { LOGIN_NARROW_MEDIA } from './loginStyles';

const useStyles = makeStyles({
  shell: {
    display: 'grid',
    gridTemplateRows: '56px minmax(0, 1fr)',
    gridTemplateAreas: '"header" "main"',
    height: '100dvh',
    ...shorthands.overflow('hidden'),
    backgroundColor: tokens.colorNeutralBackground3,
    backgroundImage: `radial-gradient(circle at 50% 20%, color-mix(in srgb, ${tokens.colorBrandBackground2} 65%, transparent), transparent 42%)`,
  },

  skipLink: {
    position: 'fixed',
    top: '-60px',
    left: tokens.spacingHorizontalL,
    zIndex: 100,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.borderRadius(0, 0, tokens.borderRadiusLarge, tokens.borderRadiusLarge),
    ':focus': {
      top: 0,
    },
  },

  header: {
    gridArea: 'header',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
    ...shorthands.padding(0, tokens.spacingHorizontalXL),
    color: tokens.colorNeutralForeground1,
    backgroundColor: `color-mix(in srgb, ${tokens.colorNeutralBackground1} 96%, transparent)`,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow2,
    backdropFilter: 'blur(18px)',
  },

  brand: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    minWidth: 0,
    ...shorthands.overflow('hidden'),
  },

  microsoftLabel: {
    whiteSpace: 'nowrap',
  },

  productName: {
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    fontWeight: tokens.fontWeightSemibold,
  },

  // The Microsoft four-square mark. Its colours are the logo's, not theme colours.
  microsoftMark: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 7px)',
    gridTemplateRows: 'repeat(2, 7px)',
    ...shorthands.gap('1px'),
    '& i:nth-child(1)': { backgroundColor: '#f25022' },
    '& i:nth-child(2)': { backgroundColor: '#7fba00' },
    '& i:nth-child(3)': { backgroundColor: '#00a4ef' },
    '& i:nth-child(4)': { backgroundColor: '#ffb900' },
  },

  brandDivider: {
    width: '1px',
    height: '20px',
    backgroundColor: tokens.colorNeutralStroke1,
  },

  main: {
    gridArea: 'main',
    display: 'grid',
    placeItems: 'center',
    minHeight: 0,
    ...shorthands.padding(`clamp(${tokens.spacingVerticalXS}, 2vh, 48px)`, tokens.spacingHorizontalXL),
    overflowY: 'auto',
    [LOGIN_NARROW_MEDIA]: {
      alignItems: 'start',
      ...shorthands.padding(tokens.spacingVerticalL),
    },
  },

  // A screen that lays out its own full-width page (registration) rather than a centred card.
  mainFullBleed: {
    display: 'block',
    ...shorthands.padding(0),
    [LOGIN_NARROW_MEDIA]: {
      ...shorthands.padding(0),
    },
  },
});

export interface LoginShellProps {
  /** Let the screen span the main region edge to edge instead of centring a card. */
  fullBleed?: boolean;
  children: ReactNode;
}

export function LoginShell({ fullBleed = false, children }: LoginShellProps) {
  const styles = useStyles();

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#login-content-title">
        Skip to main content
      </a>
      <header className={styles.header}>
        <div className={styles.brand}>
          <span className={styles.microsoftMark} aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
          <span className={styles.microsoftLabel}>Microsoft</span>
          <span className={styles.brandDivider} aria-hidden="true" />
          <strong className={styles.productName}>Law Enforcement Portal</strong>
        </div>
      </header>

      <main className={mergeClasses(styles.main, fullBleed && styles.mainFullBleed)}>
        {children}
      </main>
    </div>
  );
}
